// app/frontend/components/EventForm/sections/ArtistSelectionSection.tsx
import { useState } from 'react';
import { Accordion, Group, Text, MultiSelect, Tooltip, ActionIcon, Stack, Avatar } from '@mantine/core';
import { IconUsersGroup, IconInfoCircle } from '@tabler/icons-react';
import { ArtistSelectionSectionProps } from '../types';

const ArtistSelectionSection: React.FC<ArtistSelectionSectionProps> = ({
  form,
  artists,
  isLoadingArtists,
  artistOptionsLoaded,
  fetchAllArtists,
}) => {
  const [tooltipOpened, setTooltipOpened] = useState(false);

  return (
    <Accordion multiple className="form-accordion">
      <Accordion.Item value="artist-info">
        <Accordion.Control>
          <Group gap="xs">
            <IconUsersGroup size={28} />
            <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Artists</Text>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <MultiSelect
            label={
              <Group gap={4} align="center">
                <Text className="mb-2">Select Artists</Text>
                <Tooltip
                  label="Only artists that have created an artist profile will appear here..."
                  multiline
                  w={220}
                  withArrow
                  position="bottom"
                  color="gray.9"
                >
                  <ActionIcon
                    variant="transparent"
                    size="sm"
                    className="mb-2"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setTooltipOpened(true)}
                    onMouseLeave={() => setTooltipOpened(false)}
                  >
                    <IconInfoCircle size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            }
            name="artistIds"
            hidePickedOptions
            disabled={isLoadingArtists}
            comboboxProps={{ shadow: 'md' }}
            placeholder={form.values.artistIds && form.values.artistIds.length > 0 ? '' : 'Choose artists'}
            styles={{
              label: { color: '#FFF7ED', fontSize: '16px' },
              input: { fontSize: '16px' },
              dropdown: { fontSize: '16px' },
            }}
            data={artists.map((artist) => ({
              value: artist.id.toString(),
              label: artist.name,
            }))}
            value={(form.values.artistIds || []).map(String)}
            onChange={(values) => {
              const numericValues = Array.isArray(values) ? values.map(Number) : [];
              form.setFieldValue('artistIds', numericValues);
            }}
            searchable
            clearable
            onDropdownOpen={() => {
              if (!artistOptionsLoaded) fetchAllArtists();
            }}
          />

          {/* Display selected artist avatars below */}
          {form.values.artistIds && form.values.artistIds.length > 0 && (
            <Stack mt="md">
              <Group style={{ gap: '4px', flexWrap: 'wrap' }}>
                {form.values.artistIds.map((id: number) => {
                  const artist = artists.find((a) => a.id === id);
                  return artist ? (
                    <Avatar
                      key={id}
                      src={artist.avatar_url}
                      alt={artist.name}
                      radius="50%"
                      size="xl"
                      style={{
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
                        border: '2px solid white',
                      }}
                    />
                  ) : null;
                })}
              </Group>
            </Stack>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default ArtistSelectionSection;
